/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 10-07-2026
 *
 * Permanente, minimale root-service rond het modelcanvas: de actieve Cytoscape-`Core` en — sinds golf
 * 4d — of er nodes op staan. De blijvende eigenaar van die referentie: permanente code (zoals `DlModel`
 * en de query-API daarop) hangt aan deze service, niet aan het component.
 *
 * `DlModelCanvasComponent` meldt zijn `cy` hier aan bij init en ruimt hem op bij destroy. Omdat de
 * canvas sessie-stabiel is en `DlModel` per geladen model wordt vervangen, werkt de query-API na een
 * model-wissel meteen weer: `DlModel` leest de `cy` telkens via `getCy()`.
 *
 * Het `leeg`-signal is er voor de eilanden naast het canvas (de simulatieknoppen): die kunnen het canvas
 * niet injecteren, en `DlModel.leeg()` leest cytoscape en is dus niet reactief. Sinds golf 4e staat het
 * eiland zelf er ook: de simulatieknoppen ronden een half ingevoerde naam eerst af, en dat is een
 * handeling op het canvas.
 */
import {computed, Injectable, signal} from '@angular/core';
import type {Core} from 'cytoscape';

/**
 * Wat de eilanden naast het canvas van het modelcanvas vragen. Het eiland kan veel meer; dit is wat er
 * buiten het canvas nodig is.
 */
export interface ModelcanvasEiland {
    /**
     * Rondt het bewerken op het canvas af, zoals een klik in het lege canvas: een half ingevoerde naam
     * wordt vastgelegd en een openstaande add-actie afgebroken.
     */
    rondBewerkingAf(): Promise<void>;

    /**
     * Begint het plaatsen van een element vanuit de tekentools. Het canvas houdt de add-actie bij en
     * plaatst hem bij de volgende klik; de knop gaat mee zodat een tweede klik erop annuleert.
     */
    startAddActie(type: string, data: Record<string, unknown>, knop: HTMLElement | null): void;

    /**
     * Schuift het model naar de plek die het normmodel voorschrijft.
     */
    snapNorm(): void;
}

@Injectable({providedIn: 'root'})
export class GlobalModelcanvasService {

    /**
     * De actieve Cytoscape-`Core` van het modelcanvas, of `null` als er (nog) geen canvas is.
     */
    private cy: Core | null = null;

    /**
     * Het aangemelde modelcanvas-eiland, of `null` als er (nog) geen canvas is.
     */
    private eiland: ModelcanvasEiland | null = null;

    /**
     * Hoeveel nodes er op het modelcanvas staan, zoals het canvas het het laatst meldde.
     */
    private readonly _aantalNodes = signal(0);

    /**
     * Staat er niets op het modelcanvas? Volgt het canvas met zijn gebruikelijke korte vertraging na een
     * reeks toevoegingen of verwijderingen.
     */
    readonly leeg = computed(() => this._aantalNodes() === 0);

    /**
     * Meldt de actieve `cy` aan. Aangeroepen door `DlModelCanvasComponent` bij init.
     */
    setCy(cy: Core): void {
        this.cy = cy;
    }

    /**
     * Ruimt de referentie op. De identiteitscheck voorkomt dat een laat-afgebroken oud canvas een
     * inmiddels nieuw aangemelde `cy` wist.
     */
    clearCy(cy: Core): void {
        if (this.cy === cy) {
            this.cy = null;
            this._aantalNodes.set(0);
        }
    }

    /**
     * Meldt het eiland aan. Aangeroepen door `DlModelCanvasComponent` bij init.
     */
    setEiland(eiland: ModelcanvasEiland): void {
        this.eiland = eiland;
    }

    /**
     * Ruimt de eilandreferentie op; zie `clearCy`.
     */
    clearEiland(eiland: ModelcanvasEiland): void {
        if (this.eiland === eiland) {
            this.eiland = null;
        }
    }

    /**
     * Rondt het bewerken op het canvas af als er een canvas is. Voor de knoppen buiten het canvas die
     * een half ingevoerde naam eerst willen vastleggen.
     */
    async rondBewerkingAf(): Promise<void> {
        await this.eiland?.rondBewerkingAf();
    }

    /**
     * Begint een add-actie vanuit de tekentools naast het canvas.
     */
    startAddActie(type: string, data: Record<string, unknown>, knop: HTMLElement | null): void {
        this.eiland?.startAddActie(type, data, knop);
    }

    /**
     * Schuift het model naar de norm; de knop staat rechtsonder, buiten het canvas.
     */
    snapNorm(): void {
        this.eiland?.snapNorm();
    }

    /**
     * Geeft de actieve modelcanvas-`cy` terug, of `null` als er (nog) geen is.
     */
    getCy(): Core | null {
        return this.cy;
    }

    /**
     * Het canvas meldt hoeveel nodes er op staan. Aangeroepen door `DlModelCanvasComponent` als het zijn
     * nodes-lijst ververst.
     */
    meldAantalNodes(aantal: number): void {
        this._aantalNodes.set(aantal);
    }
}
